import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        // Remove Content-Type for FormData so browser sets it with boundary
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

// ==================== TEMPLATE API FUNCTIONS ====================

export const TEMPLATE_TYPES = ['Introduction', 'Normal', 'Conclusion'];

/**
 * Builds a multipart body when a File/Blob background is present, otherwise sends plain JSON.
 * template_content is an array, which FormData can only carry as a string -- so it's JSON-encoded
 * here and decoded by DRF's JSONField on the way in. Mirrors communication.js's toRequestBody().
 */
const toRequestBody = (data) => {
    const background = data?.template_background_image;
    if (!(background instanceof File || background instanceof Blob)) return data;

    const formData = new FormData();
    Object.keys(data).forEach((key) => {
        const value = data[key];
        if (value === null || value === undefined) return;
        formData.append(key, key === 'template_content' ? JSON.stringify(value) : value);
    });
    return formData;
};

/**
 * Get all saved templates (Authenticated users)
 * @param {string} [templateType] - Optionally filter to one type ('Introduction'|'Normal'|'Conclusion')
 * @returns {Promise<Array>} Array of templates
 */
export const getTemplates = async (templateType) => {
    try {
        const response = await api.get('/templates/', {
            params: templateType ? { template_type: templateType } : undefined,
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a template (Admin only). 'created_by' is set server-side from the request user.
 * @param {Object} templateData
 * @param {string} templateData.template_name
 * @param {string} templateData.template_type - 'Introduction'|'Normal'|'Conclusion'
 * @param {File}   [templateData.template_background_image] - Uploaded background file
 * @param {string} [templateData.template_background_type] - 'image'|'video'
 * @param {Array}  [templateData.template_content] - Block snapshots (snake_case, see models.py)
 * @returns {Promise} The created template
 */
export const createTemplate = async (templateData) => {
    try {
        const response = await api.post('/templates/', toRequestBody(templateData));
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update a template (Admin only). Replacing/clearing the background deletes the old file.
 * @param {number} templateId
 * @param {Object} templateData - Any subset of the create fields
 * @returns {Promise} The updated template
 */
export const updateTemplate = async (templateId, templateData) => {
    try {
        const response = await api.patch(`/templates/${templateId}/`, toRequestBody(templateData));
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a template (Admin only). Also deletes its background file from storage.
 * @param {number} templateId
 */
export const deleteTemplate = async (templateId) => {
    try {
        const response = await api.delete(`/templates/${templateId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};
